/**
 * Reconciliation panel embedded in the invoice detail drawer.
 *
 * Shows the authoritative state of the invoice's work:
 *  - Frozen line-item snapshots (the PDF is generated from these)
 *  - Declared periods + linked phase
 *  - An "Attach more work" dialog that picks uninvoiced timesheets/costs
 *    belonging to the same matter and links them.
 *
 * Read-only for non-admin roles.
 */
import { useEffect, useMemo, useState } from "react"
import { Calendar, Link2, Plus } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Separator } from "@/components/ui/separator"
import { Skeleton } from "@/components/ui/skeleton"
import { useToast } from "@/hooks/use-toast"
import api from "@/lib/api"
import { ROUTES } from "@/constants/routes"
import { useDateFormat } from "@/lib/dates"
import { attachItemsToInvoice } from "@/services/invoicing"

export interface InvoiceTimesheetLine {
  id: number
  timesheet_id: number | null
  date: string | null
  description: string
  user_name: string
  hours: string
  rate: string
  amount: string
  billable: boolean
}

export interface InvoiceCostLine {
  id: number
  cost_id: number | null
  date: string | null
  description: string
  category: string
  amount: string
  billable: boolean
}

export interface InvoicePeriod {
  id: number
  matter_id: number | null
  phase_id: number | null
  start_date: string
  end_date: string
  label: string
}

export interface InvoiceForReconciliation {
  id: number
  invoice_number: string
  status: string
  matter: number | { id: number } | null
  timesheet_lines?: InvoiceTimesheetLine[]
  cost_lines?: InvoiceCostLine[]
  periods?: InvoicePeriod[]
  phase?: number | null
}

interface Props {
  invoice: InvoiceForReconciliation
  onRefresh?: () => void
  canEdit?: boolean
}

function resolveMatterId(m: InvoiceForReconciliation["matter"]): number | null {
  if (m == null) return null
  if (typeof m === "number") return m
  return m.id ?? null
}

function useFmtDate() {
  const { format } = useDateFormat()
  return (iso: string | null) => {
    if (!iso) return "—"
    const d = new Date(iso)
    return Number.isNaN(d.getTime()) ? iso : format(d)
  }
}

function fmtCurrency(v: string | number) {
  const n = typeof v === "string" ? parseFloat(v) : v
  if (!Number.isFinite(n)) return "—"
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(n)
}

interface UninvoicedTimesheet {
  id: number
  date: string
  description: string
  billable_hours: string
  rate: string
  user_name?: string
}

interface UninvoicedCost {
  id: number
  date: string
  description: string
  category: string
  amount: string
}

export function InvoiceReconciliationPanel({ invoice, onRefresh, canEdit = true }: Props) {
  const matterId = resolveMatterId(invoice.matter)
  const fmtDate = useFmtDate()

  const timesheetLines = invoice.timesheet_lines ?? []
  const costLines = invoice.cost_lines ?? []
  const periods = invoice.periods ?? []

  const linesTotal = useMemo(() => {
    const ts = timesheetLines.reduce((a, l) => a + parseFloat(l.amount || "0"), 0)
    const cs = costLines.reduce((a, l) => a + parseFloat(l.amount || "0"), 0)
    return ts + cs
  }, [timesheetLines, costLines])

  return (
    <div className="space-y-5">
      {/* Periods */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold">Periods</h3>
          {canEdit && matterId ? (
            <AttachItemsDialog
              invoice={invoice}
              matterId={matterId}
              onDone={onRefresh}
            />
          ) : null}
        </div>
        {periods.length === 0 ? (
          <p className="text-sm text-muted-foreground">No periods recorded.</p>
        ) : (
          <ul className="space-y-1.5">
            {periods.map((p) => (
              <li
                key={p.id}
                className="flex items-start gap-2 rounded-md border bg-background p-2 text-sm"
              >
                <Calendar className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0">
                  <div className="font-medium">
                    {fmtDate(p.start_date)} – {fmtDate(p.end_date)}
                  </div>
                  {p.label ? (
                    <div className="text-xs text-muted-foreground">{p.label}</div>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Separator />

      {/* Snapshot line items — authoritative source of what the PDF shows */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold">Attached work</h3>
          <span className="text-xs text-muted-foreground">
            {timesheetLines.length + costLines.length} lines · {fmtCurrency(linesTotal)}
          </span>
        </div>
        {timesheetLines.length === 0 && costLines.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No line items yet. Use <em>Attach items</em> to link work to this invoice.
          </p>
        ) : (
          <div className="rounded-md border">
            <ul className="divide-y text-sm">
              {timesheetLines.map((l) => (
                <li key={`ts-${l.id}`} className="flex items-center gap-3 p-2">
                  <span className="w-20 shrink-0 text-xs text-muted-foreground">
                    {fmtDate(l.date)}
                  </span>
                  <span className="min-w-0 flex-1 truncate">
                    {l.description || <em className="text-muted-foreground">—</em>}
                    {l.user_name ? (
                      <span className="ml-2 text-xs text-muted-foreground">
                        · {l.user_name}
                      </span>
                    ) : null}
                  </span>
                  <span className="w-16 shrink-0 text-right tabular-nums text-muted-foreground">
                    {l.hours}h
                  </span>
                  <span className="w-24 shrink-0 text-right tabular-nums">
                    {fmtCurrency(l.amount)}
                  </span>
                </li>
              ))}
              {costLines.map((l) => (
                <li key={`c-${l.id}`} className="flex items-center gap-3 bg-muted/30 p-2">
                  <span className="w-20 shrink-0 text-xs text-muted-foreground">
                    {fmtDate(l.date)}
                  </span>
                  <span className="min-w-0 flex-1 truncate">
                    {l.description || <em className="text-muted-foreground">—</em>}
                    {l.category ? (
                      <span className="ml-2 text-xs text-muted-foreground">
                        · {l.category}
                      </span>
                    ) : null}
                  </span>
                  <span className="w-16 shrink-0 text-right text-xs text-muted-foreground">
                    cost
                  </span>
                  <span className="w-24 shrink-0 text-right tabular-nums">
                    {fmtCurrency(l.amount)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}

// -----------------------------------------------------------------------------
// Attach dialog
// -----------------------------------------------------------------------------

function AttachItemsDialog({
  invoice,
  matterId,
  onDone,
}: {
  invoice: InvoiceForReconciliation
  matterId: number
  onDone?: () => void
}) {
  const { toast } = useToast()
  const fmtDate = useFmtDate()
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [timesheets, setTimesheets] = useState<UninvoicedTimesheet[]>([])
  const [costs, setCosts] = useState<UninvoicedCost[]>([])
  const [picked, setPicked] = useState<{ ts: Set<number>; cost: Set<number> }>({
    ts: new Set(),
    cost: new Set(),
  })
  const [recalc, setRecalc] = useState(false)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    async function load() {
      setLoading(true)
      try {
        const [tsRes, costRes] = await Promise.all([
          api.get(`${ROUTES.API.TIMESHEET}`, {
            params: { matter: matterId, invoiced: "false", limit: 500 },
          }),
          api.get(`${ROUTES.API.COSTS}`, {
            params: { matter: matterId, invoiced: "false", limit: 500 },
          }),
        ])
        if (cancelled) return
        setTimesheets(
          (tsRes.data.results ?? tsRes.data ?? []).map((r: any) => ({
            id: r.id,
            date: r.date,
            description: r.description,
            billable_hours: String(r.billable_hours ?? "0"),
            rate: String(r.rate ?? "0"),
            user_name: r.user_name ?? "",
          })),
        )
        setCosts(
          (costRes.data.results ?? costRes.data ?? []).map((r: any) => ({
            id: r.id,
            date: r.date,
            description: r.description,
            category: r.category ?? "",
            amount: String(r.amount ?? "0"),
          })),
        )
      } catch (e) {
        toast({
          title: "Could not load uninvoiced items",
          description: (e as Error).message ?? "Try again.",
          variant: "destructive",
        })
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, matterId])

  const selectedCount = picked.ts.size + picked.cost.size
  const canRecalc = invoice.status === "draft"

  async function attach() {
    if (selectedCount === 0) return
    setSaving(true)
    try {
      await attachItemsToInvoice(invoice.id, {
        timesheet_ids: [...picked.ts],
        cost_ids: [...picked.cost],
        recalculate_totals: canRecalc && recalc,
      })
      toast({
        title: "Attached",
        description: `Linked ${selectedCount} item(s) to ${invoice.invoice_number}.`,
      })
      setOpen(false)
      setPicked({ ts: new Set(), cost: new Set() })
      onDone?.()
    } catch (e) {
      toast({
        title: "Attach failed",
        description: (e as Error).message ?? "Check the logs.",
        variant: "destructive",
      })
    } finally {
      setSaving(false)
    }
  }

  function toggleTs(id: number) {
    setPicked((p) => {
      const next = new Set(p.ts)
      next.has(id) ? next.delete(id) : next.add(id)
      return { ...p, ts: next }
    })
  }
  function toggleCost(id: number) {
    setPicked((p) => {
      const next = new Set(p.cost)
      next.has(id) ? next.delete(id) : next.add(id)
      return { ...p, cost: next }
    })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="gap-2">
          <Plus className="h-4 w-4" />
          Attach items
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            <Link2 className="mr-2 inline-block h-4 w-4" />
            Attach uninvoiced work to #{invoice.invoice_number}
          </DialogTitle>
          <DialogDescription>
            Links existing uninvoiced timesheets / costs to this invoice. By
            default the invoice's stored amounts stay untouched — this is
            reconciliation only. Tick the recalculate box (drafts only) to
            rebuild subtotal / VAT / total from the attached items.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-full" />
            ))}
          </div>
        ) : (
          <ScrollArea className="max-h-[50vh] pr-3">
            <div className="space-y-4">
              <section>
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Timesheets ({timesheets.length})
                </h4>
                {timesheets.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nothing to attach.</p>
                ) : (
                  <ul className="divide-y rounded-md border">
                    {timesheets.map((t) => (
                      <li
                        key={t.id}
                        className="flex items-center gap-3 p-2 text-sm hover:bg-muted/40"
                      >
                        <Checkbox
                          checked={picked.ts.has(t.id)}
                          onCheckedChange={() => toggleTs(t.id)}
                          aria-label={`Select timesheet ${t.id}`}
                        />
                        <span className="w-20 shrink-0 text-xs text-muted-foreground">
                          {fmtDate(t.date)}
                        </span>
                        <span className="min-w-0 flex-1 truncate">{t.description}</span>
                        <span className="tabular-nums text-muted-foreground">
                          {t.billable_hours}h × {t.rate}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section>
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Costs ({costs.length})
                </h4>
                {costs.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nothing to attach.</p>
                ) : (
                  <ul className="divide-y rounded-md border">
                    {costs.map((c) => (
                      <li
                        key={c.id}
                        className="flex items-center gap-3 p-2 text-sm hover:bg-muted/40"
                      >
                        <Checkbox
                          checked={picked.cost.has(c.id)}
                          onCheckedChange={() => toggleCost(c.id)}
                          aria-label={`Select cost ${c.id}`}
                        />
                        <span className="w-20 shrink-0 text-xs text-muted-foreground">
                          {fmtDate(c.date)}
                        </span>
                        <span className="min-w-0 flex-1 truncate">
                          {c.description}
                          {c.category ? (
                            <span className="ml-2 text-xs text-muted-foreground">
                              · {c.category}
                            </span>
                          ) : null}
                        </span>
                        <span className="tabular-nums">{fmtCurrency(c.amount)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          </ScrollArea>
        )}

        <DialogFooter className="flex items-center justify-between gap-3">
          {canRecalc ? (
            <label className="mr-auto flex items-center gap-2 text-xs">
              <Checkbox
                checked={recalc}
                onCheckedChange={(v) => setRecalc(Boolean(v))}
              />
              <span>Recalculate invoice totals (draft only)</span>
            </label>
          ) : (
            <Label className="mr-auto text-xs text-muted-foreground">
              Invoice is {invoice.status}: totals are frozen. Attach is for audit only.
            </Label>
          )}
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={attach} disabled={saving || selectedCount === 0}>
            {saving ? "Attaching…" : `Attach ${selectedCount || ""}`.trim()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
